import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import { FIRESTORE_CUSTOMERS_TABLE } from "../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import { message } from "antd";

// Create context
const UseCustomers = createContext();

// Provider component
export const CustomersProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [newCustomers, setNewCustomers] = useState([]);
  const [error, setError] = useState(null);
  const [listeners, setListeners] = useState([]);

  // Set up listeners when the provider mounts
  useEffect(() => {
    setupListeners();

    // Cleanup listeners when unmounting
    return () => {
      listeners.forEach((listener) => listener());
    };
  }, []);

  const setupListeners = () => {
    setIsLoading(true);
    setError(null);
    const newListeners = [];

    try {
      // Active customers query
      const activeCustomersQuery = query(
        collection(firestore, FIRESTORE_CUSTOMERS_TABLE),
        where("customerRegion", "==", COMPANY_COUNTRY_CODE),
        where("status", "==", "active"),
      );

      const unsubscribeActive = onSnapshot(
        activeCustomersQuery,
        (snapshot) => {
          try {
            const customersData = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }));
            setCustomers(customersData);
            setError(null);
          } catch (err) {
            console.error("Error processing active customers snapshot:", err);
            setError(err.message);
            message.error("Failed to process active customers data");
          }
        },
        (error) => {
          console.error("Error listening to active customers:", error);
          setError(error.message);
          message.error("Failed to fetch active customers data");
        },
      );

      // New customers query
      const newCustomersQuery = query(
        collection(firestore, FIRESTORE_CUSTOMERS_TABLE),
        where("customerRegion", "==", COMPANY_COUNTRY_CODE),
        where("status", "==", "new"),
      );

      const unsubscribeNew = onSnapshot(
        newCustomersQuery,
        (snapshot) => {
          try {
            const newCustomersData = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }));
            setNewCustomers(newCustomersData);
            setIsLoading(false);
          } catch (err) {
            console.error("Error processing new customers snapshot:", err);
            setError(err.message);
            setIsLoading(false);
            message.error("Failed to process new customers data");
          }
        },
        (error) => {
          console.error("Error listening to new customers:", error);
          setError(error.message);
          setIsLoading(false);
          message.error("Failed to fetch new customers data");
        },
      );

      newListeners.push(unsubscribeActive, unsubscribeNew);
      setListeners(newListeners);
    } catch (error) {
      console.error("Error setting up customers listeners:", error);
      setError(error.message);
      setIsLoading(false);
      message.error("Failed to setup customers listeners");
    }
  };

  // Helper functions
  const getCustomerById = (id) => {
    return customers.find((customer) => customer.id === id || customer.customerId === id);
  };

  const getCustomersByRegion = (region) => {
    return customers.filter((customer) => customer.customerRegion === region);
  };

  const getCustomersByStatus = (status) => {
    if (status === "new") return newCustomers;
    return customers.filter((customer) => customer.status === status);
  };

  const searchCustomers = (searchTerm) => {
    const term = searchTerm.toLowerCase();
    return customers.filter(
      (customer) =>
        customer.customerName?.toLowerCase().includes(term) ||
        customer.customerEmail?.toLowerCase().includes(term) ||
        customer.customerId?.toLowerCase().includes(term),
    );
  };

  // Memoized: used as an effect dependency (e.g. orderpicking).
  const allCustomers = useMemo(() => [...customers, ...newCustomers], [customers, newCustomers]);

  return (
    <UseCustomers.Provider
      value={{
        isLoading,
        customers, // Active customers
        newCustomers, // New customers
        allCustomers, // All customers combined
        error,
        getCustomerById,
        getCustomersByRegion,
        getCustomersByStatus,
        searchCustomers,
      }}
    >
      {children}
    </UseCustomers.Provider>
  );
};

// Custom hook to use the customers context
export const useCustomers = () => {
  const context = useContext(UseCustomers);

  if (context === undefined) {
    throw new Error("useCustomers must be used within a CustomersProvider");
  }

  return context;
};
